import { getUserAddresses, getUserData, getVariants, localStorage, returnOrders, sessionController } from "@jog/shared"
import { JogUser, JogUserAddress, JoggOrderEnrich, ProductLine, ReturnOrderType } from "@jog/types"
import { useSession } from "next-auth/react"
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"

import { signOutUser } from "../components"

interface MeState {
    user: JogUser | null
    update: () => Promise<JogUser>
    unregisteredEmail: string | null
    setUnregisteredEmail: (email: string) => void
}

const MeContext = createContext<MeState>({} as MeState)

export const MeProvider = ({ children }: PropsWithChildren) => {
    const { status, data } = useSession()
    const [user, setUser] = useState<JogUser | null>(null)
    const [unregisteredEmail, setUnregisteredEmail] = useState<string | null>(null)
    const update = useCallback(async () => {
        const user = await getUserData()
        setUser(user)
        return user
    }, [])
    useEffect(() => {
        switch (status) {
            case "unauthenticated": {
                sessionController().clear()
                break
            }
            case "authenticated": {
                const expires = data.expires
                localStorage.setItem("name", data?.user?.name)
                localStorage.setItem("email", data?.user?.email)
                if (expires && new Date(expires).getTime() < new Date().getTime()) {
                    signOutUser()
                } else {
                    sessionController().set(data)
                    if (!user) update()
                }
            }
        }
    }, [data, status, update, user])
    const value = useMemo(
        () => ({ user, update, unregisteredEmail, setUnregisteredEmail }),
        [user, update, unregisteredEmail, setUnregisteredEmail]
    )
    return <MeContext.Provider value={value}>{children}</MeContext.Provider>
}

export const useMe = () => useContext(MeContext)

export interface RuOrder {
    orderNumber: string
    orderDate: string
    orderLines: ProductLine[]
    orderType: ReturnOrderType["order_type"]
    ruId: ReturnOrderType["run_order_id"]
}
export const useOrders = (limit = 5) => {
    const [pending, setPending] = useState(true)
    const [error, setError] = useState("")
    const [orders, setOrders] = useState<RuOrder[]>([])
    const [totalPage, setTotalPage] = useState(0)
    const [page, setPage] = useState(1)
    const getRuOrders = useCallback(async () => {
        setPending(true)
        setError("")
        await returnOrders({ limit, offset: (page - 1) * limit })
            .then((res) => {
                const orders =
                    res.orders?.map<RuOrder>((order) => ({
                        ruId: order.run_order_id,
                        orderType: order.order_type,
                        orderNumber: order.order_id,
                        orderDate: order.order_date,
                        orderLines: order.line_details.line.filter((line) => line.type === "PRODUCT") as ProductLine[],
                    })) || []
                setOrders(orders)
                setTotalPage(Math.ceil(res.total / limit))
            })
            .catch((e: Error) => {
                setError(e.message)
            })
            .finally(() => {
                setPending(false)
            })
    }, [limit, page])
    useEffect(() => {
        getRuOrders().catch(() => {})
    }, [getRuOrders])
    return { pending, error, orders, totalPage, setPage, page }
}
export const useRecentOrders = () => {
    const [pending, setPending] = useState(true)
    const [error, setError] = useState("")
    const [orders, setOrders] = useState<RuOrder[]>([])
    const getRuOrders = useCallback(async () => {
        setPending(true)
        setError("")
        await returnOrders({ limit: 3 })
            .then((res) => res.orders || [])
            .then((orders) =>
                orders.map<RuOrder>((order) => ({
                    ruId: order.run_order_id,
                    orderType: order.order_type,
                    orderNumber: order.order_id,
                    orderDate: order.order_date,
                    orderLines: order.line_details.line.filter((line) => line.type === "PRODUCT") as ProductLine[],
                }))
            )
            .then((orders) => setOrders(orders))
            .catch((e: Error) => {
                setError(e.message)
            })
            .finally(() => {
                setPending(false)
            })
    }, [])
    useEffect(() => {
        getRuOrders().catch(() => {})
    }, [getRuOrders])
    return { pending, error, orders }
}

export const useOrderEnrich = (order: RuOrder) => {
    const [pending, setPending] = useState(true)
    const [error, setError] = useState("")
    const [variants, setVariants] = useState<JoggOrderEnrich["variants"]>({})
    useEffect(() => {
        setPending(true)
        setError("")
        getVariants(order.orderLines.map((i) => i.barcode))
            .then((res) => setVariants(res.variants))
            .catch((e: Error) => {
                setError(e.message)
            })
            .finally(() => {
                setPending(false)
            })
    }, [order])
    return { pending, error, variants }
}

interface AddressesState {
    addresses: JogUserAddress[] | null
    getAddresses: () => Promise<void>
    error: string
    pending: boolean
}
const AddressesContext = createContext<AddressesState>({
    addresses: null,
    getAddresses: () => Promise.resolve(),
    pending: true,
    error: "",
})

export const AddressesProvider = ({ children }: PropsWithChildren) => {
    const [pending, setPending] = useState(true)
    const [error, setError] = useState("")
    const [addresses, setAddresses] = useState<JogUserAddress[] | null>(null)
    const getAddresses = useCallback(async () => {
        setPending(true)
        setError("")
        await getUserAddresses()
            .then((res) => res.body.sort((a, b) => +(b.defaultAddress || 0) - +(a.defaultAddress || 0)))
            .then((addresses) => setAddresses(addresses))
            .catch((e: Error) => {
                setError(e.message)
            })
            .finally(() => {
                setPending(false)
            })
    }, [])
    const value = useMemo(
        () => ({ addresses, getAddresses, error, pending }),
        [addresses, error, getAddresses, pending]
    )
    return <AddressesContext.Provider value={value}>{children}</AddressesContext.Provider>
}

export const useAddresses = () => useContext(AddressesContext)
